import axios from 'axios';
import { CONFIG } from 'constants/config';
import {
  getAuthTokenCookie,
  setAuthCookie,
  getCurrentTenant,
  getErpTokenUrl,
  redirectToErp,
  isTokenExpired,
} from 'constants/auth';

export const publicApi = axios.create({
  baseURL: CONFIG.api,
});

export const privateApi = axios.create({
  baseURL: CONFIG.api,
});

let refreshPromise = null;

async function doRefresh() {
  try {
    const { data } = await axios.get(getErpTokenUrl(), { withCredentials: true });
    setAuthCookie(data.access_token);
    return data.access_token;
  } catch {
    return undefined;
  }
}

async function refreshToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh();
  const result = await refreshPromise;
  refreshPromise = null;
  return result;
}

export async function ensureFreshAuthToken() {
  let token = getAuthTokenCookie();
  if (!token) return null;
  if (!isTokenExpired(token)) return token;
  token = await refreshToken();
  return token || null;
}

publicApi.interceptors.request.use((config) => {
  config.baseURL = config.baseURL.replace('TENANT_ID', getCurrentTenant());
  return config;
});

privateApi.interceptors.request.use(async (config) => {
  config.baseURL = config.baseURL.replace('TENANT_ID', getCurrentTenant());
  const token = await ensureFreshAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

privateApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const { response } = error;

    if (!response) return Promise.reject(error);

    if (response.status === 403) {
      redirectToErp();
      return Promise.reject(error);
    }

    if (response.status === 401) {
      if (originalRequest.isRetryAttempt) {
        redirectToErp();
        return Promise.reject(error);
      }

      const newToken = await refreshToken();
      if (!newToken) {
        redirectToErp();
        return Promise.reject(error);
      }

      // eslint-disable-next-line no-param-reassign
      originalRequest.isRetryAttempt = true;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return privateApi(originalRequest);
    }

    return Promise.reject(error);
  },
);
